/*
Widget to set either a date or month/year or just a year to be entered
Sets hidden fields for the actual date and the type of date entered.
Ex: If `bottled_date` name is passed, the hidden fields will be named `bottled_date` and `bottled_date_type`
*/

import classNames from 'classnames';
import {ReactElement, useEffect, useMemo, useState} from 'react';
import {ValueOf} from 'type-fest';
import {monthsArray} from '../../constants';
import LockIconSvg from '../LockIconSvg';

const FlexibleDateValueType = Object.freeze({
  fullDate: 'full-date',
  monthYear: 'month-year',
  yearOnly: 'year-only',
});

interface FlexibleDateMonthYearFieldProps {
  name: string;
  label: string;
  value?: FlexibleDate;
  onChange: (value, type) => void;
  error?: string;
  hidden?: boolean;
  disabled?: boolean;
}

interface FlexibleDate {
  date: string; // DD-MM-YYYY
  dateType: ValueOf<typeof FlexibleDateValueType> | '';
}

export default function FlexibleDateMonthYearField({
  name,
  label,
  value,
  onChange,
  error,
  hidden,
  disabled,
}: FlexibleDateMonthYearFieldProps): ReactElement {
  const [dateObj, setDateObj] = useState({
    day: '',
    month: '',
    year: '',
  });

  const [computedValue, setComputedValue] = useState<FlexibleDate>({
    date: '',
    dateType: '',
  });

  const daysInMonth = useMemo(() => {
    if (dateObj.month) {
      return new Date(Number(dateObj.year), Number(dateObj.month), 0).getDate();
    }
    return 31;
  }, [dateObj.month, dateObj.year]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const hasError = useMemo(() => !!error, [error]);

  useEffect(() => {
    const {day, month, year} = dateObj;
    if (value?.date && !day && !month && !year) {
      setDateObj({
        day: value.date.split('-')[0],
        month: value.date.split('-')[1],
        year: value.date.split('-')[2],
      });
    }
  }, [value, dateObj, setDateObj]);

  useEffect(() => {
    const {day, month, year} = dateObj;

    let date = '';
    let dateType = undefined;
    if (day && month && year) {
      date = `${day.padStart(2, '0')}-${month}-${year}`;
      dateType = FlexibleDateValueType.fullDate;
    } else if (month && year) {
      date = `01-${month}-${year}`;
      dateType = FlexibleDateValueType.monthYear;
    } else if (year) {
      date = `01-01-${year}`;
      dateType = FlexibleDateValueType.yearOnly;
    }
    if (date && dateType) {
      setComputedValue({
        date,
        dateType,
      });
    }
  }, [dateObj]);

  useEffect(() => {
    if (
      value.date !== computedValue.date ||
      value.dateType !== computedValue.dateType
    ) {
      onChange(computedValue.date, computedValue.dateType);
    }
  }, [value, computedValue, onChange]);

  if (hidden) return <></>;

  const {day, month, year} = dateObj;

  return (
    <>
      <p className="mt-20">
        {/* (Year, Year-Month, or Year-Month-Day) */}
        {label}
        {disabled && <LockIconSvg />}
        <input
          type="hidden"
          name={name}
          value={computedValue.date}
          onChange={() => onChange(computedValue.date, computedValue.dateType)}
        />
        <input
          type="hidden"
          name={`${name}_type`}
          value={computedValue.dateType}
          onChange={() => onChange(computedValue.date, computedValue.dateType)}
        />
      </p>
      <label className="col-sm-4 mt-10">
        <span className="form__element-text">Year</span>
        <input
          type="number"
          name="_year"
          value={year}
          className={classNames('col-sm-4 form__element', {
            'form__element-error': hasError,
          })}
          min={0}
          max={currentYear}
          placeholder="Year"
          onChange={(event) =>
            setDateObj({...dateObj, year: event.target.value})
          }
          disabled={disabled}
        />
      </label>

      <label className="col-sm-4 mt-10">
        <span className="form__element-text">Month</span>
        <select
          name="_month"
          value={month}
          className={classNames('form__element', {
            'form__element-error': hasError,
          })}
          onChange={(event) =>
            setDateObj({...dateObj, month: event.target.value})
          }
          disabled={disabled}
        >
          <option value="">Month</option>
          {monthsArray.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label className="col-sm-4 mt-10">
        <span className="form__element-text">Day</span>
        <select
          name="_day"
          value={day}
          className={classNames('form__element', {
            'form__element-error': hasError,
          })}
          onChange={(event) =>
            setDateObj({...dateObj, day: event.target.value})
          }
          disabled={disabled}
        >
          <option value="">Day</option>
          {[...Array(daysInMonth).keys()].map((day) => {
            const dayValue = String(day + 1).padStart(2, '0');
            return (
              <option key={dayValue} value={dayValue}>
                {dayValue}
              </option>
            );
          })}
        </select>
      </label>

      {hasError && (
        <label>
          <p className="error-msg mt-5">{error}</p>
        </label>
      )}
    </>
  );
}
